import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FullCV,
  Input,
  Textarea,
} from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2 } from 'lucide-react';
import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { getCurrentMonthFirstDayISO, getDateForSort } from '@/utils/dateFormatters';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import { createPersonalExperienceSchema } from '../../schemas/personalExperience.schema';

interface PersonalExperiencesFormProps {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const PersonalExperiencesForm: React.FC<PersonalExperiencesFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
}) => {
  const { t } = useTranslation();

  const personalExperienceSchema = createPersonalExperienceSchema(t);
  const personalExperiencesSchema = z.object({
    personalExperiences: z.array(personalExperienceSchema).optional(),
  });

  type PersonalExperiencesFormValues = z.infer<typeof personalExperiencesSchema>;

  const form = useForm<PersonalExperiencesFormValues>({
    resolver: zodResolver(personalExperiencesSchema),
    defaultValues: initialData || {
      personalExperiences: [
        {
          title: '',
          description: '',
          location: '',
          startDate: '',
          endDate: '',
        },
      ],
    },
    mode: 'onTouched', // Validate on first touch (blur), then on every change
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'personalExperiences',
  });

  const handleSubmit = (data: PersonalExperiencesFormValues) => {
    const sorted = {
      ...data,
      personalExperiences: [...(data.personalExperiences ?? [])].sort((a, b) =>
        getDateForSort(b.endDate, b.startDate).localeCompare(
          getDateForSort(a.endDate, a.startDate),
        ),
      ),
    };
    onSubmit(sorted as FullCV);
  };

  useValidateOnMount(form);
  // @ts-expect-error - useVocalFormUpdate expects specific form types that might not match exactly
  useVocalFormUpdate(form, 'personalExperiences');

  /**
   * Stored date ('2021' | '2021-05' | '2021-05-01') → text input display
   * ('2021' when only the year is known, else 'MM/YYYY').
   * The month is never blocking: a year alone is a valid value.
   */
  const formatDateToMonth = (date: string | undefined) => {
    if (!date || date === 'CURRENT') return '';
    if (/^\d{4}$/.test(date)) return date;
    const match = /^(\d{4})-(\d{2})/.exec(date);
    if (match) return `${match[2]}/${match[1]}`;
    // Pass through anything else (text being typed) so input is never wiped
    // mid-keystroke; validation happens on blur/save instead.
    return date;
  };

  /**
   * Text input value ('2021' | 'MM/YYYY' | 'YYYY-MM') → stored format.
   */
  const formatMonthToDate = (monthStr: string) => {
    const value = monthStr.trim();
    if (!value) return '';
    if (/^\d{4}$/.test(value)) return value;
    const slash = /^(\d{2})\/(\d{4})$/.exec(value);
    if (slash) return `${slash[2]}-${slash[1]}-01`;
    const iso = /^(\d{4})-(\d{2})$/.exec(value);
    if (iso) return `${iso[1]}-${iso[2]}-01`;
    return value;
  };

  return (
    <div className="flex flex-col gap-4 w-full justify-center items-center">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2 w-full items-center mb-4">
            <h3 className="text-lg text-primary">{t('Forms.title')}</h3>
            <h1 className="text-2xl font-bold">{t('Forms.PersonalExperience.title')}</h1>
          </div>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-6 items-stretch pb-20"
          >
            {form.formState.errors.personalExperiences?.message && (
              <div className="text-destructive text-sm">
                {form.formState.errors.personalExperiences.message}
              </div>
            )}
            {fields.map((field, index) => (
              <div key={field.id} className="flex flex-row gap-4">
                <div className="flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full mt-10">
                  {index + 1}
                </div>
                <div className="flex-1 p-2 relative bg-transparent">
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name={`personalExperiences.${index}.title`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('Forms.PersonalExperience.titleInput')}</FormLabel>
                          <FormControl>
                            <Input
                              className={`h-12 ${
                                form.formState.errors.personalExperiences?.[index]?.title
                                  ? 'border-destructive'
                                  : ''
                              }`}
                              placeholder={t('Forms.PersonalExperience.titleInputPlaceholder')}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`personalExperiences.${index}.location`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('Forms.PastExperience.location')}</FormLabel>
                          <FormControl>
                            <Input
                              className="h-12"
                              placeholder={t('Forms.PastExperience.locationPlaceholder')}
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`personalExperiences.${index}.startDate`}
                        render={({ field: { onChange, value, ...field } }) => (
                          <FormItem>
                            <FormLabel>{t('Forms.PersonalExperience.startDate')}</FormLabel>
                            <FormControl>
                              <Input
                                className={`h-12 ${
                                  form.formState.errors.personalExperiences?.[index]?.startDate
                                    ? 'border-destructive'
                                    : ''
                                }`}
                                type="text"
                                placeholder={t('Forms.PersonalExperience.startDatePlaceholder')}
                                {...field}
                                value={formatDateToMonth(value)}
                                onChange={e => {
                                  onChange(formatMonthToDate(e.target.value));
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`personalExperiences.${index}.endDate`}
                        render={({ field: { onChange, value, ...field } }) => {
                          const endDateVal = (value as string) ?? '';
                          const isCurrentMonth =
                            endDateVal !== '' &&
                            formatDateToMonth(endDateVal) ===
                              formatDateToMonth(getCurrentMonthFirstDayISO());
                          const isStillInProgress = endDateVal === 'CURRENT' || isCurrentMonth;

                          return (
                            <FormItem>
                              <div className="flex items-center justify-between">
                                <FormLabel>{t('Forms.PersonalExperience.endDate')}</FormLabel>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`pe-${index}-still-in-progress`}
                                    checked={isStillInProgress}
                                    onCheckedChange={checked => {
                                      if (checked) {
                                        onChange(getCurrentMonthFirstDayISO());
                                      } else {
                                        onChange('');
                                      }
                                    }}
                                    className="align-middle size-5"
                                  />
                                  <FormLabel
                                    htmlFor={`pe-${index}-still-in-progress`}
                                    className="text-sm font-medium cursor-pointer leading-none text-foreground"
                                  >
                                    {t('Forms.PersonalExperience.stillInProgress')}
                                  </FormLabel>
                                </div>
                              </div>
                              {isStillInProgress ? (
                                <div className="flex h-12 items-center px-3 rounded-md border border-input bg-muted/40 text-sm text-foreground">
                                  {t('Forms.PersonalExperience.stillInProgress')}
                                </div>
                              ) : (
                                <FormControl>
                                  <Input
                                    className={`h-12 ${
                                      form.formState.errors.personalExperiences?.[index]?.endDate
                                        ? 'border-destructive focus-visible:ring-destructive'
                                        : ''
                                    }`}
                                    type="text"
                                    placeholder={t('Forms.PersonalExperience.endDatePlaceholder')}
                                    {...field}
                                    value={formatDateToMonth(endDateVal)}
                                    onChange={e => {
                                      const v = e.target.value;
                                      onChange(v ? formatMonthToDate(v) : '');
                                    }}
                                  />
                                </FormControl>
                              )}
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name={`personalExperiences.${index}.description`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>
                            {t('Forms.PersonalExperience.description')}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t('Forms.PersonalExperience.descriptionPlaceholder')}
                              className={`resize-none ${
                                form.formState.errors.personalExperiences?.[index]?.description
                                  ? 'border-destructive'
                                  : ''
                              }`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="h-[1px] w-full bg-gray-200 mt-7" />
                  </div>
                </div>
                <div className="mt-7">
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => remove(index)}
                    className="shrink-0 text-destructive hover:text-destructive/90 h-12 w-12"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            ))}

            <div className="flex justify-center">
              <Button
                type="button"
                onClick={() =>
                  append({
                    title: '',
                    description: '',
                    location: '',
                    startDate: '',
                    endDate: '',
                  })
                }
                className="bg-primary text-white hover:bg-primary/90"
              >
                {t('Forms.PersonalExperience.addExperience')}
              </Button>
            </div>

            <div className="flex justify-between gap-4 mt-6">
              <Button type="button" size="lg" variant="secondary" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                size="lg"
                className="bg-primary text-white hover:bg-primary/90"
                disabled={!form.formState.isValid && form.formState.isDirty}
              >
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default PersonalExperiencesForm;
